import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { StyleSheet } from 'react-native';
import { AppView, Button, NavigationHeader, SCROLL_INDICATOR_INSETS, Spacer, rem, } from 'design-system';
import Indicator from 'design-system/Indicator';
import ListEmptyView from 'design-system/ListEmptyView';
import Modal from 'utils/packages/Modal';
import { IconLightBulb, IconWallet } from 'design-system/icons';
import { IconBankCard } from 'design-system/icons/IconBankCard';
import { IconDate } from 'design-system/icons/IconDate';
import IconLoan from 'design-system/icons/IconLoan';
import IconPlus from 'design-system/icons/IconPlus';
import { IconPresentation } from 'design-system/icons/IconPresentation';
import IconRepeat from 'design-system/icons/IconRepeat';
import { PickerModal } from 'design-system/picker/PickerModal';
import { applyToSwitching, getSwitchingQuotes } from 'actions';
import Amplitude from 'utils/amplitude';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useActionSheet } from 'hooks/useActionSheet';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import StatusBar from 'design-system/StatusBar';
import Animated, { useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import { useMarginBottom } from 'hooks/useMarginBottom';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import BroadBandQuotesHeader from '../BroadBandQuotesHeader';
import BroadbandCard from '../BroadbandCard';
import BroadbandFilters from '../BroadbandFilters';
import { averageDownloadSpeedKbps, averageMonthlyCost, contractLengthInMonths, downloadLimit, totalContractCost, totalFirstYearCost, totalUpfrontCost, } from '../BroadbandSorting';
import { isWeb } from '../../../../../constants';
const keyExtractor = (item) => item.data.id.toString();
const options = [
    { label: 'First Year Costs', value: 'First Year Costs', icon: <IconLoan /> },
    {
        label: 'Avg. Monthly Costs',
        value: 'Avg. Monthly Costs',
        icon: <IconRepeat />,
    },
    {
        label: 'Total Contract Costs',
        value: 'Total Contract Costs',
        icon: <IconWallet />,
    },
    { label: 'Setup Costs', value: 'Setup Costs', icon: <IconBankCard /> },
    { label: 'Contract Length', value: 'Contract Length', icon: <IconDate /> },
    { label: 'Speed', value: 'Speed', icon: <IconLightBulb /> },
    { label: 'Usage', value: 'Usage', icon: <IconPresentation /> },
];
const BroadBandQuotes = ({ navigation, route, }) => {
    const { height, paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const colors = useColors();
    const scrollY = useSharedValue(0);
    const actionSheet = useActionSheet();
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const onPressAddNewQuote = useCallback(() => {
        actionSheet.showActionSheetWithOptions({
            title: 'Would you like to get a new quote?',
            options: ['Yes, let’s do it', 'Cancel'],
            cancelButtonIndex: 1,
        }, async (index) => {
            if (index !== 0)
                return;
            const body = {
                partner: 'BROADBANDCHOICES',
                type: 'BROADBAND',
            };
            const response = await dispatch(getSwitchingQuotes(body));
            if (response?.payload) {
                navigation.replace('PostCode', { data: response.payload });
            }
        });
    }, [actionSheet]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerRight: () => (<NavigationHeaderRightIconButton onPress={onPressAddNewQuote}>
          <IconPlus color={colors.text.primary}/>
        </NavigationHeaderRightIconButton>),
        });
    }, [colors.text.primary, onPressAddNewQuote]);
    const enquiries = useAppSelector((store) => store.switching.enquiries);
    const isFetchingResults = useAppSelector((store) => store.switching.isFetchingResults);
    useEffect(() => {
        setState((state) => ({
            ...state,
            results: enquiries.slice().sort(averageMonthlyCost),
        }));
    }, [enquiries]);
    const [state, setState] = useState({
        results: enquiries.slice().sort(averageMonthlyCost),
        defaultFilters: {
            tvDeals: false,
            minimumSpeed: 10000,
            hasSim: false,
            includeCostOfNeLine: false,
            homePhoneOnly: false,
            contractLength: [1, 12, 18, 24],
        },
        selectedSortingIndex: 1,
        selectedOption: null,
        filterState: {
            speedOptions: 0,
            phoneCallsOptions: 0,
            isTVincluded: false,
            homePhoneOnly: false,
            isMobileSim: false,
            isCostOfNewLine: false,
            isBroadbandStandard: true,
            isBroadbandFibre: true,
            isMonthlyUsageLimited: true,
            isMonthlyUsageUnlimited: true,
            contractLength: [1, 12, 18, 24],
        },
    });
    useEffect(() => {
        Amplitude.logEvent('Broadband.Quotes');
    }, []);
    const dispatch = useAppDispatch();
    const onInfoPressed = useCallback((data) => {
        const { params } = route;
        const { enquiryId } = params;
        navigation.navigateDeprecated('BroadbandQuote', { data, enquiryId });
        Amplitude.logEvent('Broadband.openQuote');
    }, [route]);
    const onPressSwitch = useCallback((data) => {
        const { params } = route;
        const { enquiryId } = params;
        Promise.resolve(dispatch(applyToSwitching(enquiryId, data.id))).then((response) => {
            if (response?.payload?.webview?.url) {
                Links.openLink(response.payload.webview.url);
                Amplitude.logEvent('Broadband.RedirectToDeal');
            }
            return response;
        });
    }, [route]);
    const onPositionChange = useCallback((selectedSortingOptionIndex) => {
        if (options[selectedSortingOptionIndex].value === 'Contract Length') {
            setState((state) => ({
                ...state,
                results: state.results.sort(contractLengthInMonths),
            }));
        }
        if (options[selectedSortingOptionIndex].value === 'First Year Costs') {
            setState((state) => ({
                ...state,
                results: state.results.sort(totalFirstYearCost),
            }));
        }
        if (options[selectedSortingOptionIndex].value === 'Avg. Monthly Costs') {
            setState((state) => ({
                ...state,
                results: state.results.sort(averageMonthlyCost),
            }));
        }
        if (options[selectedSortingOptionIndex].value === 'Total Contract Costs') {
            setState((state) => ({
                ...state,
                results: state.results.sort(totalContractCost),
            }));
        }
        if (options[selectedSortingOptionIndex].value === 'Setup Costs') {
            setState((state) => ({
                ...state,
                results: state.results.sort(totalUpfrontCost),
            }));
        }
        if (options[selectedSortingOptionIndex].value === 'Speed') {
            setState((state) => ({
                ...state,
                results: state.results.sort(averageDownloadSpeedKbps),
            }));
        }
        if (options[selectedSortingOptionIndex].value === 'Usage') {
            setState((state) => ({
                ...state,
                results: state.results.sort(downloadLimit),
            }));
        }
        setState((state) => ({
            ...state,
            selectedSortingIndex: selectedSortingOptionIndex,
        }));
    }, []);
    const onPressSelector = useCallback(() => {
        PickerModal.show({
            pickerOptions: options,
            onSelected: (_value, index) => {
                onPositionChange(index);
            },
            selectedValue: options[state.selectedSortingIndex],
            selectedIconFill: colors.gradients.lightPink.colors[0],
        }, {
            title: 'Sort by',
        });
        Amplitude.logEvent('Broadband.OpenSorting');
    }, [
        colors.gradients.lightPink.colors,
        onPositionChange,
        state.selectedSortingIndex,
    ]);
    const onFiltering = useCallback((filter, value, filterState) => {
        setState((state) => {
            const filters = { ...state.defaultFilters, [filter]: value };
            return {
                ...state,
                defaultFilters: filters,
                filterState,
            };
        });
    }, []);
    const marginBottom = useMarginBottom('marginBottom', undefined, styles.closeButton);
    const onPressFilters = useCallback(() => {
        Modal.show(<BroadbandFilters onFiltering={onFiltering} state={state.filterState}/>, {
            scrollable: {
                maxHeight: height * 0.6,
                outsideContent: (<Button brand title="Close" style={marginBottom} onPress={() => {
                        Modal.hide();
                    }}/>),
            },
            title: 'Filter',
        });
        Amplitude.logEvent('Broadband.OpenFilters');
    }, [onFiltering, state.filterState, height, marginBottom]);
    const renderItem = useCallback(({ item, index }) => {
        const isShowingNewLineCosts = state.defaultFilters.includeCostOfNeLine;
        return (<>
          {index > 0 && <Spacer.H16 />}
          <BroadbandCard item={item} includeCostofNewLine={isShowingNewLineCosts} onInfoPressed={onInfoPressed} onPressSwitch={onPressSwitch}/>
        </>);
    }, [onInfoPressed, onPressSwitch, state.defaultFilters.includeCostOfNeLine]);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, styles.contentContainerStyle);
    const renderContent = useMemo(() => {
        const { results, defaultFilters } = state;
        let resultsArray = [...results];
        if (defaultFilters.hasSim) {
            resultsArray = resultsArray.filter((item) => {
                if (item.data.dealTypes && item.data.dealTypes.length) {
                    if (item.data.dealTypes.filter((item) => item.includes('Mobile')).length)
                        return true;
                    return false;
                }
                return false;
            });
        }
        if (!defaultFilters.tvDeals)
            resultsArray = resultsArray.filter((item) => !item.data.tv);
        if (defaultFilters.homePhoneOnly)
            resultsArray = resultsArray.filter((item) => item.data.homephone && !item.data.broadband && !item.data.tv);
        if (defaultFilters.contractLength) {
            resultsArray = resultsArray.filter((item) => defaultFilters.contractLength.indexOf(item.data.contractLengthInMonths) > -1);
        }
        resultsArray = resultsArray.filter((item) => {
            if (item?.data?.broadband?.downloadSpeedInBytes) {
                if (item.data.broadband.downloadSpeedInBytes > defaultFilters.minimumSpeed)
                    return true;
                return false;
            }
            return false;
        });
        return (<Animated.FlatList data={resultsArray} initialNumToRender={4} renderItem={renderItem} onEndReachedThreshold={0.8} maxToRenderPerBatch={8} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} keyExtractor={keyExtractor} onScroll={scrollHandler} ListEmptyComponent={isFetchingResults ? (<Indicator dark style={styles.someMarginTop}/>) : (<ListEmptyView onCard title="No results" subTitle={'Update your search to see\nsomething here'}/>)} contentContainerStyle={[contentContainerStyle, paddingHorizontalStyle]}/>);
    }, [
        contentContainerStyle,
        isFetchingResults,
        paddingHorizontalStyle,
        renderItem,
        scrollHandler,
        state,
    ]);
    return (<AppView withFrame={false}>
      <StatusBar animated barStyle="dark-content"/>
      <NavigationHeader scrollY={scrollY} title="Your broadband deals" withFrame>
        <BroadBandQuotesHeader scrollY={scrollY} onPress={onPressSelector} onPressFilters={onPressFilters} label={options[state.selectedSortingIndex].label}/>
        <Spacer h={8}/>
      </NavigationHeader>
      {renderContent}
    </AppView>);
};
const styles = StyleSheet.create({
    closeButton: {
        marginHorizontal: rem(16),
    },
    contentContainerStyle: {
        paddingTop: rem(8),
    },
    someMarginTop: {
        marginTop: rem(16),
    },
});
const BroadBandQuotesScreen = BroadBandQuotes;
export default BroadBandQuotesScreen;
